/**
 * ExerciseDetailDrawer — floating drawer showing exercise metadata.
 * Trainer-only, page-scoped to TrainerWorkoutDetailPage in v1.
 *
 * Future: add mode?: 'trainer' | 'member' prop to support member-side adoption
 * without forking this component (member variant would hide the Edit footer action).
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bookmark, Pencil, Play, X } from '../../bravosIcons';
import { getYouTubeEmbedUrl, getYouTubeThumbnail, isYouTubeUrl } from '../../../../utils/youtubeUtils';
import { getExecutionStepsForDisplay, splitTags } from '../../../../utils/exerciseDisplayUtils';
import { useFocusTrap } from '../../../../hooks/useFocusTrap';
import type { Exercise } from '../../../../types/dataModel';
import './ExerciseDetailDrawer.css';

export interface ExerciseDetailDrawerProps {
  exercise: Exercise | null;
  open: boolean;
  onClose: () => void;
  /** Ref of the Details button that triggered the drawer — focus is returned here on close. */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  /** When set, enables Edit exercise and calls this handler (trainer workout detail). */
  onRequestEdit?: () => void;
  /** When false, the scrim is omitted (parent may render one shared layer for view ↔ edit). @default true */
  renderScrim?: boolean;
}

export function ExerciseDetailDrawer({
  exercise,
  open,
  onClose,
  triggerRef,
  onRequestEdit,
  renderScrim = true,
}: ExerciseDetailDrawerProps) {
  const drawerRef = useRef<HTMLElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  // Reset inline video when exercise changes.
  useEffect(() => { setVideoPlaying(false); }, [exercise?.id]);

  // Focus management: focus close button on open, return focus to trigger on close.
  useEffect(() => {
    if (open) {
      const raf = requestAnimationFrame(() => closeBtnRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    } else {
      triggerRef?.current?.focus();
    }
  }, [open, triggerRef]);

  // Escape closes the drawer.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);


  useFocusTrap(drawerRef as React.RefObject<HTMLElement | null>, open);

  if (!open || !exercise) return null;

  const exerciseTitleId = `edd-title-${exercise.id}`;
  const videoUrl = exercise.video && String(exercise.video).trim() !== '' ? exercise.video : null;
  const hasVideo = !!videoUrl;
  const ytThumb = hasVideo ? getYouTubeThumbnail(videoUrl!) : null;
  const isYouTube = hasVideo && isYouTubeUrl(videoUrl!);
  const embedAutoplay = isYouTube && videoUrl ? getYouTubeEmbedUrl(videoUrl, { autoplay: true }) : null;

  const muscleTags = splitTags(exercise.muscleGroup);
  const equipTags = splitTags(exercise.equipment);
  const hasTagGroups = muscleTags.length > 0 || equipTags.length > 0;

  const structuredSteps = getExecutionStepsForDisplay(exercise.description, exercise.steps);


  const drawer = (
    <>
      {renderScrim ? <div className="edd-scrim" aria-hidden="true" onClick={onClose} /> : null}

      {/* Drawer */}
      <aside
        ref={drawerRef}
        className="edd-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={exerciseTitleId}
      >
        {/* Header */}
        <div className="edd-header">
          <div className="edd-eyebrow">
            <span className="edd-eyebrow-dot" aria-hidden="true" />
            Exercise details
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="edd-close-btn"
            aria-label="Close"
            onClick={onClose}
          >
            <X width={14} height={14} strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="edd-body">
          {/* Video / placeholder slot */}
          <div className="edd-video-slot">
            {/* Inline video player once play is clicked */}
            {hasVideo && videoPlaying && (
              isYouTube && embedAutoplay ? (
                <iframe
                  src={embedAutoplay}
                  title={`Video: ${exercise.name}`}
                  className="edd-video-slot-iframe"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <video
                  src={videoUrl!}
                  className="edd-video-slot-native"
                  controls
                  autoPlay
                  playsInline
                >
                  Your browser does not support the video tag.
                </video>
              )
            )}

            {/* Thumbnail / placeholder shown before play */}
            {!videoPlaying && (
              <>
                {/* Image fallback when no video */}
                {!hasVideo && exercise.image && (
                  <img className="edd-video-slot-img" src={exercise.image} alt="" loading="lazy" />
                )}

                {/* YouTube thumbnail */}
                {hasVideo && isYouTube && ytThumb && (
                  <img
                    className="edd-video-slot-img"
                    src={ytThumb}
                    alt=""
                    loading="lazy"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                )}

                {/* Play button — plays inline */}
                {hasVideo && (
                  <button
                    type="button"
                    className="edd-video-play-btn"
                    aria-label={`Play video: ${exercise.name}`}
                    onClick={() => setVideoPlaying(true)}
                  >
                    <span className="edd-video-play-circle">
                      <Play
                        width={24}
                        height={24}
                        fill="currentColor"
                        color="var(--twd-ink)"
                        strokeWidth={0}
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                )}

                {/* HD demo pill */}
                {hasVideo && (
                  <div className="edd-video-meta-bar">
                    <span className="edd-video-pill">HD demo</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Exercise name */}
          <h2 id={exerciseTitleId} className="edd-name">
            {exercise.name}
          </h2>

          {/* Tag groups */}
          {hasTagGroups && (
            <div className="edd-tag-groups">
              {muscleTags.length > 0 && (
                <div className="edd-tag-group">
                  <span className="edd-tag-group-label">Muscle</span>
                  <div className="edd-tag-row">
                    {muscleTags.map((t) => (
                      <span key={t} className="edd-tag edd-tag-muscle">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {equipTags.length > 0 && (
                <div className="edd-tag-group">
                  <span className="edd-tag-group-label">Equipment</span>
                  <div className="edd-tag-row">
                    {equipTags.map((t) => (
                      <span key={t} className="edd-tag">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Execution steps */}
          <div>
            <div className="edd-exec-header">
              <span className="edd-exec-label">Execution</span>
              {structuredSteps.length > 0 && (
                <span className="edd-exec-badge">{structuredSteps.length} steps</span>
              )}
            </div>

            {structuredSteps.length === 0 ? (
              <p className="edd-steps-empty">No execution steps yet.</p>
            ) : (
              <ol className="edd-steps">
                {structuredSteps.map((step, i) => (
                  <li key={i} className="edd-step">
                    <span className="edd-step-text">{step}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="edd-footer">
          {/* Bookmark — inert v1 */}
          <button
            type="button"
            className="edd-footer-bookmark"
            aria-label="Save to favorites"
            aria-disabled="true"
            tabIndex={0}
            onClick={(e) => e.preventDefault()}
          >
            <Bookmark width={16} height={16} strokeWidth={2} aria-hidden="true" />
          </button>

          <button
            type="button"
            className="edd-footer-edit"
            aria-disabled={!onRequestEdit}
            tabIndex={0}
            onClick={(e) => {
              if (!onRequestEdit) {
                e.preventDefault();
                return;
              }
              onRequestEdit();
            }}
          >
            <Pencil width={14} height={14} strokeWidth={2} aria-hidden="true" />
            Edit exercise
          </button>
        </div>
      </aside>

    </>
  );

  return createPortal(drawer, document.body);
}
